import { useState, useEffect } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Target,
  Activity,
  TrendingUp,
  Clock,
  Heart,
  Flame,
  Droplets,
  Moon,
  AlertCircle,
  ChevronRight,
  Info,
  Zap,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/popover";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";
import welcomeBg from "@/projects/Admin_Bite_Balance/bite-balance/src/assets/welcome-bg.jpg";

interface MedicationDose {
  time: string;
  frequency: string;
}

interface Medication {
  id: string;
  name: string;
  dosage: string;
  doses: MedicationDose[];
  active: boolean;
}

const Home = () => {
  const { toast } = useToast();
  const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const [medications, setMedications] = useState<Medication[]>([]);
  const [takenDoses, setTakenDoses] = useState<Set<string>>(new Set());
  const [isFasting, setIsFasting] = useState(false);
  const [fastingStartTime, setFastingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastFastingDuration, setLastFastingDuration] = useState<string | null>(
    null
  );
  const [timeSinceLastMeal, setTimeSinceLastMeal] =
    useState<string>("No meals logged");

  useEffect(() => {
    const loadMedications = () => {
      const stored = localStorage.getItem("medications");
      if (stored) {
        setMedications(JSON.parse(stored));
      }

      // Load today's taken doses
      const today = new Date().toDateString();
      const takenToday = localStorage.getItem(`takenDoses_${today}`);
      if (takenToday) {
        setTakenDoses(new Set(JSON.parse(takenToday)));
      }
    };

    // Load on mount
    loadMedications();

    // Reload when window gains focus (user returns to page)
    const handleFocus = () => {
      loadMedications();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Load fasting state from localStorage
  useEffect(() => {
    const savedFastingStart = localStorage.getItem("fastingStartTime");
    const savedLastDuration = localStorage.getItem("lastFastingDuration");
    if (savedFastingStart) {
      setFastingStartTime(parseInt(savedFastingStart));
      setIsFasting(true);
    }
    if (savedLastDuration) {
      setLastFastingDuration(savedLastDuration);
    }
  }, []);

  // Calculate time since last meal
  useEffect(() => {
    const updateTimeSinceLastMeal = () => {
      const lastMealTime = localStorage.getItem("lastMealTime");
      if (lastMealTime) {
        const diff = Date.now() - new Date(lastMealTime).getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeSinceLastMeal(`${hours}h ${minutes}m`);
      } else {
        setTimeSinceLastMeal("No meals logged");
      }
    };
    updateTimeSinceLastMeal();
    const interval = setInterval(updateTimeSinceLastMeal, 60000);
    return () => clearInterval(interval);
  }, []);

  // Update elapsed time every second when fasting
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isFasting && fastingStartTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - fastingStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isFasting, fastingStartTime]);

  const handleStartFasting = () => {
    const now = Date.now();
    setFastingStartTime(now);
    setIsFasting(true);
    setElapsedTime(0);
    localStorage.setItem("fastingStartTime", now.toString());
    toast({
      title: "Fasting Started",
      description: "Your fasting timer has begun. Good luck!",
    });
  };

  const handleEndFasting = () => {
    const duration = formatElapsedTime(elapsedTime);
    setLastFastingDuration(duration);
    setIsFasting(false);
    setFastingStartTime(null);
    setElapsedTime(0);
    localStorage.removeItem("fastingStartTime");
    localStorage.setItem("lastFastingDuration", duration);
    toast({
      title: "Fasting Ended",
      description: `You fasted for ${duration}. Great job!`,
    });
  };

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const getTargetSeconds = () => {
    if (userProfile.fastingWindow?.includes("16:8")) {
      return 16 * 3600;
    }
    if (userProfile.fastingWindow?.includes("18:6")) {
      return 18 * 3600;
    }
    if (userProfile.fastingWindow?.includes("14:10")) {
      return 14 * 3600;
    }
    return 16 * 3600; // default
  };

  const handleMarkDoseTaken = (medId: string, doseIndex: number) => {
    const doseKey = `${medId}_${doseIndex}`;
    const newTakenDoses = new Set(takenDoses);
    newTakenDoses.add(doseKey);
    setTakenDoses(newTakenDoses);

    // Save to localStorage with today's date
    const today = new Date().toDateString();
    localStorage.setItem(
      `takenDoses_${today}`,
      JSON.stringify([...newTakenDoses])
    );
  };

  const isDoseTaken = (medId: string, doseIndex: number) => {
    return takenDoses.has(`${medId}_${doseIndex}`);
  };

  // Get active medications with their doses
  const activeMedications = medications
    .filter(med => med.active)
    .flatMap(med =>
      med.doses.map((dose, idx) => ({
        medId: med.id,
        name: med.name,
        dosage: med.dosage,
        frequency: dose.frequency,
        time: dose.time,
        doseIndex: idx,
        taken: isDoseTaken(med.id, idx),
      }))
    );

  // Bite Balance Score (single unified score)
  const biteBalanceScore = 74; // 0-100

  // Additional health metrics
  const additionalMetrics = [
    {
      name: "PRAL Score",
      value: "-12.5",
      unit: "mEq/day",
      color: "bg-success",
      icon: TrendingUp,
    },
    {
      name: "Carb-to-Fiber Ratio",
      value: "8:1",
      unit: "ratio",
      color: "bg-warning",
      icon: Activity,
    },
    {
      name: "Fat Quality Indicator",
      value: "72%",
      unit: "quality",
      color: "bg-info",
      icon: Heart,
    },
    {
      name: "Micro Diversity Score",
      value: "18",
      unit: "/30",
      color: "bg-chart-3",
      icon: Flame,
    },
  ];

  // Weekly calorie data - Intake vs Burned
  const weeklyCalorieData = [
    { day: "Mon", intake: 1850, burned: 520 },
    { day: "Tue", intake: 1920, burned: 480 },
    { day: "Wed", intake: 1750, burned: 550 },
    { day: "Thu", intake: 1980, burned: 490 },
    { day: "Fri", intake: 2100, burned: 600 },
    { day: "Sat", intake: 1890, burned: 530 },
    { day: "Sun", intake: 1450, burned: 420 },
  ];

  // Weekly sleep data (replacing steps)
  const weeklySleepData = [
    { day: "Mon", hours: 7.5 },
    { day: "Tue", hours: 8.2 },
    { day: "Wed", hours: 6.8 },
    { day: "Thu", hours: 8.5 },
    { day: "Fri", hours: 7.2 },
    { day: "Sat", hours: 9.0 },
    { day: "Sun", hours: 8.5 },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden pb-24">
      {/* Blurred Background Image */}
      <div
        className="fixed inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />

      {/* Overlay for better contrast */}
      <div className="fixed inset-0 bg-background/70" />

      <div className="relative z-10 mx-auto max-w-md space-y-4 p-4">
        {/* Header with User Greeting */}
        <div className="pb-1 pt-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Welcome back,
          </h2>
          <h1 className="text-2xl font-semibold text-foreground">
            {userProfile.name || "Rajesh Shah"}
          </h1>
        </div>

        {/* Bite Balance Score */}
        <Card
          className="cursor-pointer rounded-xl border-2 border-border bg-card p-5 shadow-md transition-shadow hover:shadow-lg"
          onClick={() => (window.location.href = "/insights/bite-balance")}
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-foreground">
                Bite Balance Score
              </h3>
              <p className="text-xs text-muted-foreground">
                Today,{" "}
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="flex items-center gap-6">
            {/* Single BBS Ring */}
            <div className="flex flex-col items-center">
              <div className="relative h-32 w-32">
                <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="hsl(var(--muted))"
                    strokeWidth="8"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${(biteBalanceScore / 100) * 314} 314`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black text-primary">
                    {biteBalanceScore}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    / 100
                  </span>
                </div>
              </div>
            </div>

            {/* Score info */}
            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground">Your BBS</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Combines meal glycemic load, body composition, and activity into
                one score.
              </p>
              <span className="mt-2 inline-block rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                Good
              </span>
            </div>
          </div>
        </Card>
        {/* Glucose Spike Summary */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              <h3 className="text-base font-semibold text-foreground">
                Glucose Spike Levels
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => (window.location.href = "/meals")}
            >
              Details <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-2.5">
            {[
              { meal: "Breakfast", level: "Medium", time: "8:30 AM" },
              { meal: "Lunch", level: "Low", time: "1:00 PM" },
              { meal: "Snack", level: "Low", time: "4:30 PM" },
              { meal: "Dinner", level: "High", time: "7:30 PM" },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg bg-muted/20 p-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                      item.level === "High"
                        ? "bg-destructive/15"
                        : item.level === "Medium"
                          ? "bg-amber-500/15"
                          : "bg-emerald-500/15"
                    }`}
                  >
                    <Zap
                      className={`h-4 w-4 ${
                        item.level === "High"
                          ? "text-destructive"
                          : item.level === "Medium"
                            ? "text-amber-500"
                            : "text-emerald-500"
                      }`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {item.meal}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {item.time}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-sm font-bold ${
                    item.level === "High"
                      ? "text-destructive"
                      : item.level === "Medium"
                        ? "text-amber-500"
                        : "text-emerald-500"
                  }`}
                >
                  {item.level}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Insights Card */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-foreground">
              Health Insights
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Carb-Fiber Balance - value 0.12, Excellent ≤7, Good ≤12, Moderate ≤20, Poor >20 (lower is better, max ~25) */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() => (window.location.href = "/insights/carb-fiber")}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-success/10 border-success/30 flex h-8 w-8 items-center justify-center rounded-lg border-2">
                  <Activity className="text-success h-4 w-4" />
                </div>
              </div>
              <p className="mb-1 text-xs font-bold text-foreground">
                Carb-Fiber Ratio
              </p>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-success text-sm font-bold">
                  Excellent
                </span>
                <span className="text-[10px] text-muted-foreground">
                  (0.12)
                </span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/40">
                <div className="absolute inset-0 flex">
                  <div className="bg-success/30 h-full w-[28%]" />
                  <div className="bg-chart-2/30 h-full w-[20%]" />
                  <div className="bg-warning/30 h-full w-[32%]" />
                  <div className="h-full w-[20%] bg-destructive/30" />
                </div>
                <div
                  className="bg-success absolute top-0 h-full w-1 rounded-full shadow-sm"
                  style={{ left: `${Math.min((0.12 / 25) * 100, 98)}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-[9px] text-muted-foreground">Low</span>
                <span className="text-[9px] text-muted-foreground">High</span>
              </div>
            </Card>

            {/* Fat Quality - value 0.14, Good ≤0.15, Moderate 0.15-0.30, Poor >0.30 (lower is better, max ~0.40) */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() => (window.location.href = "/insights/fat-quality")}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-primary/30 bg-primary/10">
                  <Heart className="h-4 w-4 text-primary" />
                </div>
              </div>
              <p className="mb-1 text-xs font-bold text-foreground">
                Fat Quality
              </p>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-success text-sm font-bold">Good</span>
                <span className="text-[10px] text-muted-foreground">
                  (0.14)
                </span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/40">
                <div className="absolute inset-0 flex">
                  <div className="bg-success/30 h-full w-[38%]" />
                  <div className="bg-warning/30 h-full w-[37%]" />
                  <div className="h-full w-[25%] bg-destructive/30" />
                </div>
                <div
                  className="bg-success absolute top-0 h-full w-1 rounded-full shadow-sm"
                  style={{ left: `${Math.min((0.14 / 0.4) * 100, 98)}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-[9px] text-muted-foreground">Low</span>
                <span className="text-[9px] text-muted-foreground">High</span>
              </div>
            </Card>

            {/* Sugar Load - value 0.18, Good ≤0.10, Moderate 0.10-0.20, Poor >0.20 (lower is better, max ~0.30) */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() => (window.location.href = "/insights/sugar-load")}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-warning/10 border-warning/30 flex h-8 w-8 items-center justify-center rounded-lg border-2">
                  <Flame className="text-warning h-4 w-4" />
                </div>
              </div>
              <p className="mb-1 text-xs font-bold text-foreground">
                Sugar Load
              </p>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-warning text-sm font-bold">Moderate</span>
                <span className="text-[10px] text-muted-foreground">
                  (0.18)
                </span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/40">
                <div className="absolute inset-0 flex">
                  <div className="bg-success/30 h-full w-[33%]" />
                  <div className="bg-warning/30 h-full w-[34%]" />
                  <div className="h-full w-[33%] bg-destructive/30" />
                </div>
                <div
                  className="bg-warning absolute top-0 h-full w-1 rounded-full shadow-sm"
                  style={{ left: `${Math.min((0.18 / 0.3) * 100, 98)}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-[9px] text-muted-foreground">Low</span>
                <span className="text-[9px] text-muted-foreground">High</span>
              </div>
            </Card>

            {/* Protein-Fiber Ratio - value 3.2, Excellent ≤2.5, Good ≤4.0, Moderate ≤6.0, Poor >6.0 (lower is better, max ~8) */}
            <Card
              className="cursor-pointer rounded-xl border border-border bg-card/50 p-4 transition-shadow hover:shadow-md"
              onClick={() =>
                (window.location.href = "/insights/protein-fiber-ratio")
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-chart-3/10 border-chart-3/30 flex h-8 w-8 items-center justify-center rounded-lg border-2">
                  <TrendingUp className="text-chart-3 h-4 w-4" />
                </div>
              </div>
              <p className="mb-1 text-xs font-bold text-foreground">
                Protein-Fiber Ratio
              </p>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-chart-2 text-sm font-bold">Good</span>
                <span className="text-[10px] text-muted-foreground">(3.2)</span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/40">
                <div className="absolute inset-0 flex">
                  <div className="bg-success/30 h-full w-[31%]" />
                  <div className="bg-chart-2/30 h-full w-[19%]" />
                  <div className="bg-warning/30 h-full w-[25%]" />
                  <div className="h-full w-[25%] bg-destructive/30" />
                </div>
                <div
                  className="bg-chart-2 absolute top-0 h-full w-1 rounded-full shadow-sm"
                  style={{ left: `${Math.min((3.2 / 8) * 100, 98)}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-[9px] text-muted-foreground">Low</span>
                <span className="text-[9px] text-muted-foreground">High</span>
              </div>
            </Card>
          </div>
        </Card>

        {/* Intermittent Fasting Card - Conditional */}
        {userProfile.intermittentFasting ===
          "Yes — I follow a fasting window" &&
          userProfile.fastingWindow && (
            <Card className="from-chart-2/5 to-chart-2/10 rounded-xl border border-border/50 bg-gradient-to-br p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-chart-2/20 border-chart-2/40 flex h-11 w-11 items-center justify-center rounded-lg border-2">
                    <Clock className="text-chart-2 h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-foreground">
                        Intermittent Fasting
                      </h3>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:bg-chart-2/20 h-5 w-5 rounded-full"
                          >
                            <Info className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="pointer-events-auto w-80 p-4"
                          align="start"
                        >
                          <div className="space-y-4">
                            <div>
                              <h4 className="mb-3 text-sm font-semibold text-foreground">
                                Gut Health Benefits:
                              </h4>
                              <div className="space-y-2.5">
                                <div className="flex items-start gap-2.5">
                                  <div className="bg-chart-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                                  <div>
                                    <p className="mb-0.5 text-xs font-semibold text-foreground">
                                      MMC Activation
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Enables gut cleansing waves to clear
                                      bacteria and debris
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                  <div className="bg-chart-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                                  <div>
                                    <p className="mb-0.5 text-xs font-semibold text-foreground">
                                      Reduced Inflammation
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Gut lining heals and inflammatory
                                      responses decrease
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                  <div className="bg-chart-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                                  <div>
                                    <p className="mb-0.5 text-xs font-semibold text-foreground">
                                      Microbiome Balance
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Promotes beneficial bacteria growth and
                                      diversity
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                  <div className="bg-chart-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                                  <div>
                                    <p className="mb-0.5 text-xs font-semibold text-foreground">
                                      Insulin Sensitivity
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Better blood sugar regulation reduces gut
                                      stress
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="rounded-lg bg-muted/20 p-3">
                              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                                Fasting Windows:
                              </h4>
                              <div className="space-y-2 text-xs">
                                <div className="flex items-center justify-between border-b border-border/30 py-1.5">
                                  <span className="font-medium">
                                    12-14 hours
                                  </span>
                                  <span className="text-muted-foreground">
                                    Beginner
                                  </span>
                                </div>
                                <div className="flex items-center justify-between border-b border-border/30 py-1.5">
                                  <span className="text-chart-2 font-medium">
                                    14-16 hours
                                  </span>
                                  <span className="text-muted-foreground">
                                    Optimal
                                  </span>
                                </div>
                                <div className="flex items-center justify-between py-1.5">
                                  <span className="font-medium">
                                    16-18 hours
                                  </span>
                                  <span className="text-muted-foreground">
                                    Advanced
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {userProfile.fastingWindow?.split(" ")[0]} Goal
                    </p>
                  </div>
                </div>
              </div>

              {/* Timer Display */}
              {isFasting ? (
                <div className="mb-4 rounded-lg bg-background/60 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Fasting Duration
                    </span>
                    <span className="text-chart-2 text-3xl font-bold">
                      {formatElapsedTime(elapsedTime)}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(
                      (elapsedTime / getTargetSeconds()) * 100,
                      100
                    )}
                    className="mb-3 h-2.5"
                  />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Started:{" "}
                      {fastingStartTime
                        ? new Date(fastingStartTime).toLocaleTimeString(
                            "en-US",
                            { hour: "numeric", minute: "2-digit" }
                          )
                        : "--"}
                    </span>
                    <span className="text-muted-foreground">
                      Target: {userProfile.fastingWindow?.split(" ")[0]}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mb-4 rounded-lg bg-background/60 p-4 text-center">
                  {lastFastingDuration ? (
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">
                        Last fasting window
                      </p>
                      <p className="text-chart-2 text-2xl font-bold">
                        {lastFastingDuration}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Start your fasting timer when ready
                    </p>
                  )}
                </div>
              )}

              {/* Action Button */}
              {isFasting ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 w-full rounded-lg border-destructive text-sm font-medium text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleEndFasting}
                >
                  End Fasting
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-chart-2 hover:bg-chart-2/90 h-10 w-full rounded-lg text-sm font-medium text-white"
                  onClick={handleStartFasting}
                >
                  Start Fasting
                </Button>
              )}
            </Card>
          )}

        {/* Today's Nutrition Summary */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Today's Nutrition
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => (window.location.href = "/meals")}
            >
              View All <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                    <Flame className="h-4 w-4 text-accent" />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg font-semibold text-foreground">
                        1,450
                      </span>
                      <span className="text-sm text-muted-foreground">
                        / 2,000 kcal
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      Calorie Intake
                    </span>
                  </div>
                </div>
                <span className="text-sm font-medium text-muted-foreground">
                  72%
                </span>
              </div>
              <Progress value={72} className="h-1.5" />
            </div>

            <div className="grid grid-cols-4 gap-2.5">
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-1 text-base font-semibold">65g</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Protein
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-2 text-base font-semibold">180g</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Carbs
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-3 text-base font-semibold">48g</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Fat
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-4 text-base font-semibold">25g</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Fiber
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Activity Summary */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Today's Activity
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => (window.location.href = "/fitness")}
            >
              View All <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                    <Flame className="h-4 w-4 text-accent" />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg font-semibold text-foreground">
                        420
                      </span>
                      <span className="text-sm text-muted-foreground">
                        / 600 kcal
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      Calories Burned
                    </span>
                  </div>
                </div>
                <span className="text-sm font-medium text-muted-foreground">
                  70%
                </span>
              </div>
              <Progress value={70} className="h-1.5" />
            </div>

            <div className="grid grid-cols-4 gap-2.5">
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-1 text-base font-semibold">
                  8,206
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Steps
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-2 text-base font-semibold">68</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  BPM
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-3 text-base font-semibold">8.5h</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Sleep
                </div>
              </div>
              <div className="rounded-lg bg-muted/20 py-3 text-center">
                <div className="text-chart-4 text-base font-semibold">45</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Active Min
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Fasting Status Card */}
        {userProfile.intermittentFasting ===
          "Yes — I follow a fasting window" &&
          userProfile.fastingWindow && (
            <Card
              className="border-chart-2/30 bg-chart-2/5 cursor-pointer rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md"
              onClick={() => (window.location.href = "/gut-rest")}
            >
              <div className="flex items-center gap-3">
                <div className="bg-chart-2/15 border-chart-2/30 flex h-10 w-10 items-center justify-center rounded-full border">
                  <Clock className="text-chart-2 h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground">
                    Intermittent Fasting
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {userProfile.fastingWindow?.split(" ")[0]} Goal
                  </p>
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-xs text-muted-foreground">
                  Time since last meal
                </p>
                <p className="text-chart-2 mt-1 text-2xl font-bold">
                  {timeSinceLastMeal}
                </p>
              </div>
              {timeSinceLastMeal !== "No meals logged" &&
                (() => {
                  const targetHours = parseInt(
                    userProfile.fastingWindow?.split(":")[0] || "16"
                  );
                  const lastMealTime = localStorage.getItem("lastMealTime");
                  if (!lastMealTime) {
                    return null;
                  }
                  const diff = Date.now() - new Date(lastMealTime).getTime();
                  const hoursElapsed = diff / (1000 * 60 * 60);
                  const progress = Math.min(
                    (hoursElapsed / targetHours) * 100,
                    100
                  );
                  return <Progress value={progress} className="mt-3 h-1.5" />;
                })()}
            </Card>
          )}

        {/* Medication Reminders */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Medication Reminders
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => (window.location.href = "/medications")}
            >
              Manage <ChevronRight className="ml-0.5 h-4 w-4" />
            </Button>
          </div>

          {activeMedications.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground">
                No active medications
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => (window.location.href = "/medications")}
              >
                Add Medication
              </Button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {activeMedications.map((med, index) => (
                <div
                  key={`${med.medId}_${med.doseIndex}`}
                  className={`flex items-center gap-3 rounded-lg border-2 p-3.5 ${
                    med.taken
                      ? "border-muted bg-muted/10 opacity-60"
                      : "border-border bg-muted/20"
                  }`}
                >
                  <div
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${
                      med.taken
                        ? "bg-muted"
                        : index % 2 === 0
                          ? "bg-info"
                          : "bg-secondary"
                    }`}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium ${
                        med.taken
                          ? "text-muted-foreground line-through"
                          : "text-foreground"
                      }`}
                    >
                      {med.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {med.frequency} • {med.time} • {med.dosage}
                    </p>
                  </div>
                  {!med.taken && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs font-medium"
                      onClick={() =>
                        handleMarkDoseTaken(med.medId, med.doseIndex)
                      }
                    >
                      Done
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Calorie Intake vs Burned */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Calorie Intake vs Burned
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Weekly comparison
              </p>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyCalorieData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  cursor={{ stroke: "hsl(var(--border))" }}
                />
                <Line
                  type="monotone"
                  dataKey="intake"
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--chart-1))", r: 4 }}
                  name="Intake"
                />
                <Line
                  type="monotone"
                  dataKey="burned"
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--chart-2))", r: 4 }}
                  name="Burned"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="mt-3 flex items-center justify-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="bg-chart-1 h-2.5 w-2.5 rounded-full" />
              <span className="text-[11px] text-muted-foreground">Intake</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="bg-chart-2 h-2.5 w-2.5 rounded-full" />
              <span className="text-[11px] text-muted-foreground">Burned</span>
            </div>
          </div>

          {/* Weight Loss Insight */}
          {(userProfile.weightLossGoal === "Yes" ||
            userProfile.weightLossGoal === "Not Sure") &&
            (() => {
              const avgIntake =
                weeklyCalorieData.reduce((s, d) => s + d.intake, 0) /
                weeklyCalorieData.length;
              const avgBurned =
                weeklyCalorieData.reduce((s, d) => s + d.burned, 0) /
                weeklyCalorieData.length;
              const deficit = avgIntake - avgBurned;
              const isHelping = deficit < 1500;

              return (
                <div
                  className={`mt-4 rounded-lg border-l-[3px] px-3.5 py-3 ${
                    isHelping
                      ? "border-l-secondary bg-secondary/5"
                      : "border-l-warning bg-warning/5"
                  }`}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    {isHelping ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-secondary" />
                    ) : (
                      <AlertTriangle className="text-warning h-3.5 w-3.5" />
                    )}
                    <span
                      className={`text-xs font-bold ${isHelping ? "text-secondary" : "text-warning"}`}
                    >
                      {isHelping
                        ? "On Track for Weight Loss"
                        : "Not in Deficit Zone"}
                    </span>
                  </div>
                  <p className="mb-2.5 text-[11px] leading-relaxed text-muted-foreground">
                    {isHelping
                      ? "Your calorie balance supports gradual weight loss. Keep it up!"
                      : `Try reducing intake or increasing burn by ~${Math.round(deficit - 1400)} kcal to support weight loss.`}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-chart-1 text-[10px] font-bold">
                      {Math.round(avgIntake)} in
                    </span>
                    <span className="text-[10px] text-muted-foreground">−</span>
                    <span className="text-chart-2 text-[10px] font-bold">
                      {Math.round(avgBurned)} out
                    </span>
                    <span className="text-[10px] text-muted-foreground">=</span>
                    <span
                      className={`text-[10px] font-bold ${isHelping ? "text-secondary" : "text-warning"}`}
                    >
                      {Math.round(deficit)} net kcal
                    </span>
                  </div>
                </div>
              );
            })()}
        </Card>

        {/* Weekly Sleep Chart */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Weekly Sleep
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Daily sleep hours
              </p>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklySleepData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  domain={[0, 10]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  cursor={{ fill: "hsl(var(--muted))" }}
                />
                <Bar
                  dataKey="hours"
                  fill="hsl(var(--info))"
                  radius={[8, 8, 0, 0]}
                  name="Sleep Hours"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Quick Actions */}
      </div>
    </div>
  );
};

export default Home;
